
import { Component, Vue } from 'vue-property-decorator';
import { collection, deleteField, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ROOT_ACTIONS } from '@/store/actions';
import { ProgramFormula } from '@/types';

type TypeTemplateRow = {
  assType: string;
  label: string;
  nlTemplateId: string;
  frTemplateId: string;
};

type SessionFormulaRow = {
  sessionTemplate: string;
  formula: string;
};

type DynamicsWebhookConfig = {
  nlLanguageCode: string;
  typeTemplates: TypeTemplateRow[];
  sessionFormulas: SessionFormulaRow[];
};

const CONFIG_PATH = ['config', 'dynamics-webhook'] as const;
const SECRET_PATH = ['config-secrets', 'dynamics-webhook'] as const;
const WEBHOOK_SECRET_HEADER = 'x-itinera-webhook-secret';

type WebhookSecretConfig = {
  secret: string;
  enforceOnProvision: boolean;
};

const DEFAULT_CONFIG: DynamicsWebhookConfig = {
  nlLanguageCode: '117270000',
  typeTemplates: [],
  sessionFormulas: [],
};

@Component
export default class DynamicsSettings extends Vue {
  loading = {
    fetch: false,
    save: false,
    secret: false,
  };

  programTemplates: { id: string; title: string }[] = [];

  config: DynamicsWebhookConfig = JSON.parse(JSON.stringify(DEFAULT_CONFIG));

  get webhookUrl() {
    return 'https://europe-west1-itinera-342308.cloudfunctions.net/provisionParticipantProgram';
  }

  get requestBodySample() {
    return `{
  "participant_email": "manu_renders@msn.com",
  "participant_firstname": "Manu",
  "participant_lastname": "Renders",
  "participant_phone": "+32 477 91 13 62",
  "participant_lang": "117270000",
  "coach_email": "erik.leenders@itinera-talentcoaching.be",
  "ass_sesiontemplate": "OUT - Alg. Reg/Régl.Gén. +opzeg/préavis",
  "ass_type": "100000000",
  "startdate": "2026-08-11",
  "ass_phase1_end_date": "2026-10-10T22:00:00Z",
  "ass_phase2_end_date": "2027-02-10T23:00:00Z",
  "ass_phase3_end_date": "2027-08-10T22:00:00Z"
}`;
  }

  get statusWebhookUrl() {
    return 'https://europe-west1-itinera-342308.cloudfunctions.net/updateProgramStatusFromDynamics';
  }

  get suspendBodySample() {
    return `{
  "action": "SUSPEND",
  "participant_email": "manu_renders@msn.com",
  "hub_program_id": "",
  "early_end_date": "2026-11-30T23:00:00Z"
}`;
  }

  get archiveBodySample() {
    return `{
  "action": "ARCHIVE",
  "participant_email": "manu_renders@msn.com",
  "hub_program_id": ""
}`;
  }

  secretHeader = WEBHOOK_SECRET_HEADER;
  showSecret = false;
  secretConfig: WebhookSecretConfig = { secret: '', enforceOnProvision: false };

  async loadSecretConfig() {
    const snap = await getDoc(doc(firestore, ...SECRET_PATH));
    const data = (snap.exists() ? snap.data() : {}) as Partial<WebhookSecretConfig>;
    this.secretConfig = {
      secret: data.secret || '',
      enforceOnProvision: data.enforceOnProvision === true,
    };
  }

  async generateSecret() {
    this.loading.secret = true;
    try {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      this.secretConfig.secret = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      this.showSecret = true;
      await this.saveSecretConfig();
    } finally {
      this.loading.secret = false;
    }
  }

  async saveSecretConfig() {
    try {
      await setDoc(doc(firestore, ...SECRET_PATH), {
        secret: this.secretConfig.secret,
        enforceOnProvision: !!this.secretConfig.secret && this.secretConfig.enforceOnProvision,
        updatedAt: new Date(),
      });
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'success',
        text: 'Beveiliging opgeslagen',
      });
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: error,
      });
    }
  }

  get formulaOptions() {
    return Object.values(ProgramFormula).map((value) => ({
      value,
      text: value,
    }));
  }

  async copyText(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'success',
        text: 'Gekopieerd',
      });
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: 'Kopiëren mislukt',
      });
    }
  }

  async mounted() {
    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, [
      { text: 'Instellingen', target: '/admin/settings/dynamics' },
      { text: 'Dynamics', target: '/admin/settings/dynamics' },
    ]);
    await this.load();
  }

  addTypeTemplateRow() {
    this.config.typeTemplates.push({
      assType: '',
      label: '',
      nlTemplateId: '',
      frTemplateId: '',
    });
  }

  removeTypeTemplateRow(index: number) {
    this.config.typeTemplates.splice(index, 1);
  }

  addSessionFormulaRow() {
    this.config.sessionFormulas.push({
      sessionTemplate: '',
      formula: ProgramFormula.ALGEMEEN_OPZEG,
    });
  }

  removeSessionFormulaRow(index: number) {
    this.config.sessionFormulas.splice(index, 1);
  }

  async load() {
    this.loading.fetch = true;
    try {
      const templatesSnap = await getDocs(collection(firestore, 'program-templates'));
      this.programTemplates = templatesSnap.docs
        .map((templateDoc) => ({
          id: templateDoc.id,
          title: (templateDoc.data().title as string) || templateDoc.id,
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

      const configSnap = await getDoc(doc(firestore, ...CONFIG_PATH));
      if (configSnap.exists()) {
        const data = configSnap.data() as Partial<DynamicsWebhookConfig>;
        this.config = {
          nlLanguageCode: data.nlLanguageCode || DEFAULT_CONFIG.nlLanguageCode,
          typeTemplates: Array.isArray(data.typeTemplates)
            ? data.typeTemplates.map((row) => ({ ...row, label: row.label || '' }))
            : [],
          sessionFormulas: Array.isArray(data.sessionFormulas) ? data.sessionFormulas : [],
        };
      } else {
        this.config = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
      }
      await this.loadSecretConfig();
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: error,
      });
    } finally {
      this.loading.fetch = false;
    }
  }

  async save() {
    this.loading.save = true;
    try {
      const payload: DynamicsWebhookConfig = {
        nlLanguageCode: (this.config.nlLanguageCode || '').trim(),
        typeTemplates: this.config.typeTemplates
          .map((row) => ({
            assType: (row.assType || '').trim(),
            label: (row.label || '').trim(),
            nlTemplateId: (row.nlTemplateId || '').trim(),
            frTemplateId: (row.frTemplateId || '').trim(),
          }))
          .filter((row) => row.assType && (row.nlTemplateId || row.frTemplateId)),
        sessionFormulas: this.config.sessionFormulas
          .map((row) => ({
            sessionTemplate: (row.sessionTemplate || '').trim(),
            formula: (row.formula || '').trim(),
          }))
          .filter((row) => row.sessionTemplate && row.formula),
      };

      // Drop the old fields, which had the two meanings swapped.
      await setDoc(
        doc(firestore, ...CONFIG_PATH),
        { ...payload, templates: deleteField(), assTypes: deleteField() },
        { merge: true }
      );
      this.config = payload;

      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'success',
        text: 'Dynamics koppelingen opgeslagen',
      });
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: error,
      });
    } finally {
      this.loading.save = false;
    }
  }
}
